/**
 * createMutation — runtime factory for codegen-emitted mutation hooks.
 *
 * Analog of `createHook` for queries. Composes TanStack `useMutation` with
 * the optimistic-update pattern: cancel affected queries → snapshot → apply
 * optimistic patch → run fetch → roll back on error → invalidate on settle.
 *
 * Codegen emits one .gen.ts file per mutation that calls this factory with
 * a `MutationRuntimeSpec` derived from `sdk.manifest.yml`. Engineers never
 * touch this file — it's runtime infrastructure, not per-mutation code.
 *
 * Mirrors the optimistic-write contract in
 * `src/core/mutations/useSdkMutation.ts` §L1960 (cancelQueries + snapshot
 * + setQueryData + rollback), simplified for the codegen-emitted case
 * where the spec carries one optimistic target per mutation.
 */
import { hashKey, useMutation, useQueryClient, } from '@tanstack/react-query';
import { markNextWrite } from '../../devtool/channels/cache-write.js';
import { sdkEvents } from '../../devtool/events.js';
import { useRenderTelemetryDev } from '../../devtool/hooks/useRenderTelemetryDev.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';
import { registerMutationSpec } from '../../devtool/registry/spec-registry.js';
import { useSdkNetworkScope, useSdkViewerScope } from '../../provider/SdkRuntimeContext.js';
import { fetchWithHttpTelemetry } from '../../transport/http-request.js';
import { throwHttpError } from '../http-error.js';
import { writeNormalizedEntitySnapshot } from '../internal/normalized-entity-write.js';
import { claimOptimisticRollback, ownsOptimisticRollback, readOptimisticRollbackToken, releaseOptimisticRollback, restoreOptimisticRollback, } from '../internal/optimistic-rollback-owner.js';
import { applyViewerRelationshipMutation, rollbackViewerRelationshipMutation, viewerRelationshipScope, } from '../internal/viewer-relationship.js';
/** UUID for Idempotency-Key — works when only getRandomValues is polyfilled (RN). */
function createIdempotencyKey() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        crypto.getRandomValues(bytes);
    }
    else {
        for (let i = 0; i < 16; i++) {
            bytes[i] = (Math.random() * 256) | 0;
        }
    }
    const b6 = bytes[6] ?? 0;
    const b8 = bytes[8] ?? 0;
    bytes[6] = (b6 & 0x0f) | 0x40;
    bytes[8] = (b8 & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
/**
 * Monotonic counter per process — used as the discriminator on every
 * `mutation:phase` event so concurrent invocations of the same spec don't
 * collide into a single span. Reset only on module reload; HMR is safe
 * because the translator keys spans by `<specName>|<mutationKey>` and a
 * fresh counter just means fresh spans.
 */
let _mutationSeq = 0;
/**
 * Build the TanStack mutation options for a given spec + QueryClient.
 * Exposed for tests that drive the mutation lifecycle via `MutationObserver`
 * without mounting React. The production hook just wraps this in
 * `useMutation(_buildMutationOptions(spec, qc))`.
 *
 * Internal — do NOT import from app code. Public surface is `createMutationHook`.
 */
export function _buildMutationOptions(spec, qc, viewerScope = '', networkScope = 'unbound') {
    // Two-stage FIFO pipeline keyed by vars-reference. Concurrent
    // `mutate(vars)` calls sharing the SAME vars object reference would
    // otherwise corrupt correlation:
    //
    //   onMutate1 → push #1     | Q1 = [#1]
    //   onMutate2 → push #2     | Q1 = [#1, #2]
    //   mutationFn1 → pop  #1   | Q1 = [#2],     Q2 = [#1]
    //   mutationFn2 → pop  #2   | Q1 = [],       Q2 = [#1, #2]
    //   onSettled1  → pop  #1   |                Q2 = [#2]
    //   onSettled2  → pop  #2   |                Q2 = []
    //
    // The mutationFn moves its correlation from Q1 to Q2 so onError +
    // onSettled (which run AFTER mutationFn for the same invocation) drain
    // from Q2 in matching order. Per-invocation order across TanStack's
    // lifecycle is guaranteed by the await chain — onMutate completes
    // before mutationFn fires; mutationFn completes before onError/onSettled.
    //
    // Vars may be a primitive (rare for SDK use, but allowed), so we can't
    // use WeakMap. Entries are removed in onSettled; queue size is bounded
    // by in-flight mutation count.
    const onMutateQueue = new Map();
    const mutationFnQueue = new Map();
    function pushTo(q, v, corr) {
        const arr = q.get(v);
        if (arr === undefined)
            q.set(v, [corr]);
        else
            arr.push(corr);
    }
    function popFrom(q, v) {
        const arr = q.get(v);
        if (arr === undefined || arr.length === 0)
            return undefined;
        const corr = arr.shift();
        if (arr.length === 0)
            q.delete(v);
        return corr;
    }
    function peekFrom(q, v) {
        return q.get(v)?.[0];
    }
    function newCorrelation() {
        _mutationSeq += 1;
        return {
            id: _mutationSeq,
            mutationKey: [`${spec.specName}#${_mutationSeq}`],
            startMs: Date.now(),
        };
    }
    return {
        mutationFn: async (vars) => {
            // Pop from the onMutate queue → this is THIS invocation's correlation.
            // Move it to the mutationFn queue so onError + onSettled can drain
            // in matching order. If onMutate was skipped (defensive: TanStack
            // always runs onMutate, but tests can construct an observer without
            // one), synthesize a fresh correlation.
            let corr = popFrom(onMutateQueue, vars);
            if (corr === undefined) {
                corr = newCorrelation();
            }
            pushTo(mutationFnQueue, vars, corr);
            sdkEvents.emit('mutation:phase', {
                specName: spec.specName,
                phase: 'fetch',
                mutationKey: corr.mutationKey,
                vars,
            });
            const url = spec.resolveUrl(vars);
            // Every mutation is a write (POST/PUT/PATCH/DELETE). The backend's
            // idempotency middleware — mounted on a subset of write routes (e.g.
            // `/v1/theses/{signature}/reply`) — rejects writes that omit
            // `Idempotency-Key` with HTTP 400 `WRITE_MISSING_IDEMPOTENCY_KEY`.
            // A fresh UUID per invocation satisfies the requirement and lets a
            // replayed request dedupe server-side. This is the documented home for
            // the key — see `platform/adapter.ts` (T8202): idempotencyKey lives in
            // the mutation wrapper via `crypto.randomUUID()` (polyfilled on RN).
            const headers = {
                Accept: 'application/json',
                // RN often has getRandomValues (via react-native-get-random-values)
                // but not randomUUID — fall back so mutations don't throw before fetch.
                'Idempotency-Key': createIdempotencyKey(),
            };
            let body;
            if (spec.buildBody !== undefined) {
                const built = spec.buildBody(vars);
                if (built instanceof FormData) {
                    // Multipart: pass FormData straight through. Do NOT set
                    // Content-Type — the browser sets the multipart boundary itself.
                    body = built;
                }
                else if (built !== undefined) {
                    body = JSON.stringify(built);
                    headers['Content-Type'] = 'application/json';
                }
            }
            // Per-call AbortSignal — surfaced on `vars.signal` (codegen-emitted on
            // multipart mutation Vars; consumers writing JSON-body mutations can opt
            // in by passing `signal` alongside their vars). Plumbs straight into
            // fetch init.signal so a closing modal cancels the in-flight upload.
            const callerSignal = vars.signal;
            const signal = callerSignal instanceof AbortSignal ? callerSignal : undefined;
            const init = {
                method: spec.method,
                headers,
                credentials: 'include',
                ...(body !== undefined ? { body } : {}),
                ...(signal !== undefined ? { signal } : {}),
            };
            let httpStatus;
            try {
                const res = await fetchWithHttpTelemetry({
                    operationName: spec.specName,
                    routeTemplate: spec.path,
                    requestKind: 'mutation',
                    method: spec.method,
                    url,
                    init,
                    ...(signal === undefined ? {} : { signal }),
                    retryCount: 0,
                });
                httpStatus = res.status;
                if (!res.ok) {
                    await throwHttpError(spec.method, url, res);
                }
                const json = (await res.json());
                const unwrapped = json !== null && typeof json === 'object' && 'data' in json
                    ? json.data
                    : json;
                const result = spec.parseResponse(unwrapped);
                if (process.env.NODE_ENV !== 'production') {
                    _bumpPerf('mutation.success');
                }
                sdkEvents.emit('mutation:phase', {
                    specName: spec.specName,
                    phase: 'success',
                    mutationKey: corr.mutationKey,
                    durationMs: Date.now() - corr.startMs,
                    httpStatus,
                });
                return result;
            }
            catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    _bumpPerf('mutation.error');
                }
                sdkEvents.emit('mutation:phase', {
                    specName: spec.specName,
                    phase: 'error',
                    mutationKey: corr.mutationKey,
                    durationMs: Date.now() - corr.startMs,
                    httpStatus,
                    error: err instanceof Error ? err.message : String(err),
                });
                throw err;
            }
        },
        onMutate: async (vars) => {
            const corr = newCorrelation();
            pushTo(onMutateQueue, vars, corr);
            if (process.env.NODE_ENV !== 'production') {
                _bumpPerf('mutation.starts');
            }
            sdkEvents.emit('mutation:phase', {
                specName: spec.specName,
                phase: 'optimistic',
                mutationKey: corr.mutationKey,
                vars,
            });
            const relationshipRollback = spec.viewerRelationship === undefined
                ? undefined
                : applyViewerRelationshipMutation(qc, viewerRelationshipScope(viewerScope, networkScope), spec.viewerRelationship, vars, { source: 'optimistic', observedAtMs: Date.now() });
            if (spec.optimistic === undefined) {
                return {
                    ...(relationshipRollback === undefined ? {} : { relationshipRollback }),
                    snapshots: [],
                };
            }
            // Normalise singular-block form (legacy emit shape) to array.
            const blocks = Array.isArray(spec.optimistic) ? spec.optimistic : [spec.optimistic];
            if (blocks.length === 0) {
                return {
                    ...(relationshipRollback === undefined ? {} : { relationshipRollback }),
                    snapshots: [],
                };
            }
            // Multi-target: cancel + patch each block in declaration order. The
            // snapshot bag stores only entries actually changed by this mutation,
            // so a failed prefix-matched recipe cannot roll back unrelated rows.
            // Apply filters live inside each block's helper.
            const allSnapshots = [];
            const observedAtMs = Date.now();
            for (const block of blocks) {
                const { queryKey, apply } = block;
                const filters = block.predicate === undefined ? { queryKey } : { queryKey, predicate: block.predicate };
                // Cancel in-flight refetches so they don't overwrite the optimistic value.
                await qc.cancelQueries(filters);
                const matches = qc.getQueriesData(filters);
                // Compute the whole graph from one pre-mutation snapshot before any
                // write lands. Cross-entry helpers can therefore read a viewer
                // overlay while deriving its canonical entity delta without query
                // iteration order changing the answer.
                const patches = matches.map(([k, prev]) => [k, apply(prev, vars, k, qc, viewerScope, networkScope)]);
                for (const [index, [k, next]] of patches.entries()) {
                    const previous = matches[index]?.[1];
                    if (next === qc.getQueryData(k))
                        continue;
                    const previousToken = readOptimisticRollbackToken(qc, k);
                    const token = {};
                    // markNextWrite feeds the devtool `cache:write` channel — its
                    // only consumer (`useSdkCacheTimeline`) lives in the `/devtool`
                    // subpath, never in production bundles. Strip in prod.
                    if (process.env.NODE_ENV !== 'production') {
                        markNextWrite(qc, hashKey(k), 'optimistic');
                    }
                    if (block.entityGraph === true &&
                        (k[1] === 'entity' || k[1] === 'viewer') &&
                        next !== null &&
                        typeof next === 'object') {
                        const result = writeNormalizedEntitySnapshot(qc, k, next, {
                            source: 'optimistic',
                            observedAtMs,
                        });
                        if (result.status === 'written') {
                            claimOptimisticRollback(qc, k, token, qc.getQueryData(k));
                            allSnapshots.push({ key: k, previous, previousToken, token });
                        }
                        if (k[1] === 'viewer' && process.env.NODE_ENV !== 'production') {
                            _bumpPerf(result.status === 'stale'
                                ? 'viewerOverlay.staleWritesRejected'
                                : 'viewerOverlay.writes');
                        }
                    }
                    else {
                        qc.setQueryData(k, next);
                        claimOptimisticRollback(qc, k, token, qc.getQueryData(k));
                        allSnapshots.push({ key: k, previous, previousToken, token });
                    }
                }
            }
            return {
                ...(relationshipRollback === undefined ? {} : { relationshipRollback }),
                snapshots: allSnapshots,
            };
        },
        ...(spec.onSuccess === undefined
            ? {}
            : {
                onSuccess: (response, vars) => spec.onSuccess?.(response, vars, qc),
            }),
        onError: (_err, vars, ctx) => {
            // Peek (don't pop) — onSettled is responsible for popping. Both
            // callbacks run for the same invocation; we don't want to drain
            // the queue twice.
            const corr = peekFrom(mutationFnQueue, vars);
            if (ctx === undefined)
                return;
            rollbackViewerRelationshipMutation(qc, ctx.relationshipRollback);
            let supersededSnapshots = 0;
            for (const snapshot of [...ctx.snapshots].reverse()) {
                const { key, previous, previousToken, token } = snapshot;
                if (!ownsOptimisticRollback(qc, key, token, qc.getQueryData(key))) {
                    supersededSnapshots += 1;
                    continue;
                }
                // Rollback is also an optimistic-source write — the canonical
                // value comes back via the subsequent `onSettled` invalidate +
                // refetch, which the cache-write channel tags `rest`.
                if (process.env.NODE_ENV !== 'production') {
                    markNextWrite(qc, hashKey(key), 'optimistic');
                }
                if (previous === undefined)
                    qc.removeQueries({ queryKey: key, exact: true }); // ALLOW-STATE-OWNERSHIP: exact rollback cleanup
                // ALLOW: this is the declared rollback owner for generated mutations.
                // REASON: restoring the exact captured key avoids undeclared cache fan-out.
                else
                    qc.setQueryData(key, previous); // ALLOW-STATE-OWNERSHIP: exact mutation rollback
                restoreOptimisticRollback(qc, key, previousToken, qc.getQueryData(key));
            }
            // `rollback` fires only when an optimistic recipe was configured —
            // i.e. there was something to roll back. Pure-error paths (no
            // optimistic spec) go straight from `error` → `settled`.
            const hadOptimistic = spec.viewerRelationship !== undefined ||
                (spec.optimistic !== undefined &&
                    (Array.isArray(spec.optimistic) ? spec.optimistic.length > 0 : true));
            if (corr !== undefined && hadOptimistic) {
                if (process.env.NODE_ENV !== 'production') {
                    _bumpPerf('mutation.rollback');
                }
                sdkEvents.emit('mutation:phase', {
                    specName: spec.specName,
                    phase: 'rollback',
                    mutationKey: corr.mutationKey,
                    ...(supersededSnapshots === 0 ? {} : { supersededSnapshots }),
                });
            }
        },
        onSettled: (_data, _err, vars, ctx) => {
            // Pop the FRONT of the mutationFn queue — this invocation's
            // correlation. FIFO ordering ensures we drain in matched-mutation
            // order across concurrent calls with the same vars-reference.
            const corr = popFrom(mutationFnQueue, vars);
            if (corr !== undefined) {
                const totalMs = Date.now() - corr.startMs;
                if (process.env.NODE_ENV !== 'production') {
                    _bumpPerf('mutation.settled');
                    _observe('mutationDurationMs', totalMs);
                }
                sdkEvents.emit('mutation:phase', {
                    specName: spec.specName,
                    phase: 'settled',
                    mutationKey: corr.mutationKey,
                    durationMs: totalMs,
                });
            }
            for (const snapshot of ctx?.snapshots ?? []) {
                releaseOptimisticRollback(qc, snapshot.key, snapshot.token);
            }
            if (spec.optimistic === undefined)
                return undefined;
            const blocks = Array.isArray(spec.optimistic) ? spec.optimistic : [spec.optimistic];
            if (blocks.length === 0)
                return undefined;
            // Invalidate each target's source query key. Canonical entity observers
            // are intentionally fetchless, so entity-graph recipes carry the route
            // query that can retrieve the server value. A block with
            // `reconcile: false` opts out when its optimistic state is exact.
            return Promise.all(blocks
                .filter((block) => block.reconcile !== false)
                .map((block) => {
                if (block.reconcileQueryKey !== undefined) {
                    // ALLOW: the generated mutation declares this source query owner.
                    // REASON: canonical fetchless entities reconcile through their exact source hook.
                    const sourceFilters = {
                        queryKey: block.reconcileQueryKey,
                        ...(block.reconcileSpecName === undefined
                            ? {}
                            : {
                                predicate: (query) => query.meta?.specName === block.reconcileSpecName,
                            }),
                        refetchType: 'all',
                    };
                    return qc.invalidateQueries(sourceFilters); // ALLOW-STATE-OWNERSHIP: source owner
                }
                // ALLOW: this is the declared optimistic target for the mutation.
                // REASON: non-entity recipes reconcile only their manifest-owned query prefix.
                const targetFilters = block.predicate === undefined
                    ? { queryKey: block.queryKey }
                    : { queryKey: block.queryKey, predicate: block.predicate };
                return qc.invalidateQueries(targetFilters); // ALLOW-STATE-OWNERSHIP: mutation target
            })).then(() => undefined);
        },
        retry: 0,
    };
}
/**
 * Public factory. The hook just wraps `_buildMutationOptions` in `useMutation`.
 * Tests that need to drive the lifecycle without React should call
 * `_buildMutationOptions(spec, qc)` directly and feed it to
 * `MutationObserver`.
 */
export function createMutationHook(spec) {
    // DCE-gated devtool registration — stripped from production bundles
    // by the bundler's NODE_ENV substitution. See createHook for the
    // rationale; the registry is dev-only observability.
    if (process.env.NODE_ENV !== 'production') {
        registerMutationSpec(spec.specName, spec);
    }
    return function useMorfiMutation(options) {
        // Render telemetry — DCE-stripped from prod via the build-time
        // NODE_ENV check. See createHook.ts for the rules-of-hooks safety
        // rationale.
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useRenderTelemetryDev(spec.specName);
        }
        const qc = useQueryClient();
        const viewerScope = useSdkViewerScope();
        const networkScope = useSdkNetworkScope();
        const base = _buildMutationOptions(spec, qc, viewerScope, networkScope);
        return useMutation(options?.meta !== undefined ? { ...base, meta: options.meta } : base);
    };
}
//# sourceMappingURL=createMutation.js.map